import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  FileText,
  Users,
  CreditCard,
  Briefcase,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  Calendar,
  Store,
  BarChart3,
} from 'lucide-react';

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/admin/programs', icon: Briefcase, label: 'Programs' },
    { path: '/admin/membership', icon: CreditCard, label: 'Membership Plans' },
    { path: '/admin/members', icon: Users, label: 'Member Verification' },
    { path: '/admin/market', icon: Store, label: 'Market Place' },
    { path: '/admin/downloads', icon: FileText, label: 'Reading Analytics' },
    { path: '/admin/experts', icon: Users, label: 'Expert Panel' },
    { path: '/admin/dialogues', icon: Calendar, label: 'Dialogues' },
    { path: '/admin/research', icon: FileText, label: 'Research Papers' },
    { path: '/admin/funding', icon: Briefcase, label: 'Funding Options' },
    { path: '/admin/inquiries', icon: MessageSquare, label: 'Contact Inquiries' },
    { path: '/admin/users', icon: Users, label: 'User Management' },
  ];

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden bg-[#1E3A5F] text-white p-4 flex justify-between items-center">
        <h1 className="text-xl font-bold">CSED Admin</h1>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-white/10 rounded"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-0 left-0 h-screen bg-[#1E3A5F] text-white w-64 
            transition-transform duration-300 z-40
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <h1 className="text-2xl font-bold">CSED Admin</h1>
            <p className="text-sm text-gray-300 mt-1">Management Portal</p>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                    ${active 
                      ? 'bg-[#E87722] text-white' 
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'
                    }
                  `}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Info & Logout */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
            <div className="mb-3">
              <p className="text-sm text-gray-300">Logged in as</p>
              <p className="font-medium truncate">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-4 py-2 text-left text-gray-300 hover:bg-white/10 rounded-lg transition-colors"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="hidden lg:flex bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8f] text-white px-8 py-4 items-center justify-between shadow" data-testid="admin-top-ribbon">
            <p className="font-semibold tracking-wide">CSED Admin Console</p>
            <Link to="/" className="text-sm text-white/80 hover:text-[#E87722] transition-colors">← Back to Website</Link>
          </div>
          <main className="flex-1 p-6 lg:p-8">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
